import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import NotFound from "@/pages/NotFound";

/**
 * Route: /:vanity — but only handles /@username. React Router 7 does not
 * match a static '@' prefix INSIDE a dynamic segment, so we accept any
 * top-level path and dispatch to /404 when it doesn't start with '@'.
 *
 * On a valid `@handle` we call /api/seo/username/{handle} and use
 * `history.replaceState` to redirect to /civilization/profile/{userId}.
 */
export default function UsernameRedirect() {
    const { vanity } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("resolving"); // resolving|notfound

    const isVanity = typeof vanity === "string" && vanity.startsWith("@");
    const username = isVanity ? vanity.slice(1) : "";

    useEffect(() => {
        if (!isVanity) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await api.seoResolveUsername(username);
                if (cancelled) return;
                if (data?.canonical_path) {
                    navigate(data.canonical_path, { replace: true });
                    return;
                }
                setStatus("notfound");
            } catch {
                if (!cancelled) setStatus("notfound");
            }
        })();
        return () => { cancelled = true; };
    }, [isVanity, username, navigate]);

    // Not a vanity URL → let the generic 404 handle it (keeps behavior
    // for random paths like /foo the same as before).
    if (!isVanity) return <NotFound />;

    if (status === "resolving") {
        return (
            <div
                data-testid="username-redirect-loading"
                className="min-h-screen bg-[var(--color-bg)] text-text-secondary grid place-items-center"
            >
                <span className="text-sm">Resolving @{username}…</span>
            </div>
        );
    }

    return (
        <div
            data-testid="username-redirect-notfound"
            className="min-h-screen bg-[var(--color-bg)] text-text-secondary grid place-items-center px-6"
        >
            <div className="max-w-md text-center">
                <p className="text-[10px] uppercase tracking-widest mb-2">404</p>
                <h1 className="text-2xl font-semibold text-text-main">@{username} not found</h1>
                <p className="mt-3 text-sm">
                    This username is either not claimed or the profile is private.
                </p>
                <button
                    onClick={() => navigate("/civilization/leaderboard")}
                    className="mt-6 rounded-full gradient-bg-cta px-5 py-2 text-sm text-white glow-primary"
                >
                    Browse the leaderboard
                </button>
            </div>
        </div>
    );
}
